import Vue from "vue";
import Component from "vue-class-component";
import { ISaveSatisfaction, Step } from "../../../shared/interfaces/satisfaction.interface";
import { NotificationServiceInstance } from "../../../shared/services/notification.service";
import { ParametresInstance } from "../../Controls/Parameters";
import { ServicesFactoryInstance } from "../../Controls/ServicesFactory";

@Component
export default class ModalEnqueteSatisfactionPst extends Vue {
	public visible = false; // Visibilité du modal
	public currentStep = 0; // Étape active
	public hoveredValue: number | null = null; // Valeur survolée
	public callbackYes?: () => void; // Callback après fermeture

	// Données de l'enquête de satisfaction
	public satisfactionData: ISaveSatisfaction = {
		NoteRecommandation: null,
		NoteBulletin: null,
		NoteReactivite: null,
		NoteQualite: null,
		Commentaire: null
	};

	// Étapes de l'enquête de satisfaction
	public steps: { [key: string]: Step } = {
		0: {
			active: true,
			text: "",
			key: ""
		},
		1: {
			active: false,
			text: "Sur une échelle de 0 à 10 recommanderiez-vous notre <strong>SERVICE DE PAIE SOUS-TRAITÉE</strong> à un(e) collègue ou à une autre étude ?",
			key: "NoteRecommandation"
		},
		2: {
			active: false,
			text: "Dans quelle mesure êtes-vous satisfait(e) de la précision et de la fiabilité des bulletins de paie fournis par votre gestionnaire ?",
			key: "NoteBulletin"
		},
		3: {
			active: false,
			text: "Les délais de traitement et la réactivité de votre gestionnaire répondent-ils à vos attentes ?",
			key: "NoteReactivite"
		},
		4: {
			active: false,
			text: "Comment évaluez-vous la disponibilité et la qualité des réponses de votre gestionnaire lorsque vous avez une question ?",
			key: "NoteQualite"
		},
		5: {
			active: false,
			text: "Avez-vous une remarque ou un point d’amélioration à partager concernant le service de paie sous-traitée?",
			key: "Commentaire"
		},
		6: {
			active: false,
			text: "",
			key: ""
		}
	}

	public mounted(): void {
		this.setMessageAcceuil();
		this.visible = true;
	}

	public init(options?: { callbackYes?: () => void }): void {
		this.callbackYes = options?.callbackYes;
		this.visible = true;
	}

	/**
	 * Méthode pour définir le message d'accueil
	 * @returns {void}
	 */
	public setMessageAcceuil(): void {
		if (ParametresInstance.societeCourante?.DernierExercice == null) {
			return;
		}

		const dernierExercice = new Date(ParametresInstance.societeCourante.DernierExercice);
		const currentMonth = dernierExercice.getMonth() + 1;
		let currentYear = dernierExercice.getFullYear();
		let trimestre = "";

		if (currentMonth && currentYear) {
			if (currentMonth <= 3) {
                trimestre = "quatrième";
                currentYear = currentYear - 1;
			} else if (currentMonth <= 6) {
				trimestre = "premier";
			} else if (currentMonth <= 9) {
				trimestre = "deuxième";
			} else {
				trimestre = "troisième";
			}
		}

		this.steps[0].text = `Aidez-nous à améliorer votre expérience en répondant à quelques questions sur la paie de ce <span class="trimestre">${trimestre} trimestre ${currentYear}</span>.`;
	}

	/**
     * Méthode pour masquer le modal
     * @returns {void}
     */
	public hide(): void {
    	this.$emit("closeModal");

		if (this.currentStep == 6) {
			this.callbackYes?.();
		}
	}

	/**
	 * Retourne le texte de l'étape active
	 * @returns {string}
	 */
	public get getActiveText(): string {
		const activeStep = Object.values(this.steps).find(step => step.active);

		return activeStep ? activeStep.text : "";
	}

	/**
	 * Retourne la valeur de l'étape active (nps avec les étoiles)
	 * Utile pour garder la valeur null au début de l'étape
	 * @returns {number}
	 */
	public get currentStepRate(): number {
		return this.satisfactionData[this.steps[this.currentStep].key] ?? 0;
	}

	/**
	 * Définit la valeur de l'étape active (nps avec les étoiles)
	 * @param value {number}
	 * @returns {void}
	 */
	public set currentStepRate(value: number) {
		this.satisfactionData[this.steps[this.currentStep].key] = value || null;
	}

	/**
	 * Définit la valeur pour l'étape 1 - nps1 range de 0 à 10
	 * @param value {number} - La valeur du NPS 1
	 * @returns {void}
	 */
	public selectNpsValue(value: number): void {
		this.satisfactionData.NoteRecommandation = value;
	}

	/**
	 * Passe à l'étape suivante
	 * @returns {void}
	 */
	public nextStep(): void {
		this.currentStep += 1;
		this.setActiveStep();
	}

	/**
	 * Revient à l'étape précédente
	 * @returns {void}
	 */
	public previousStep(): void {
		if (this.currentStep > 1) {
			this.currentStep -= 1;
			this.setActiveStep();
		}
	}

	/**
	 * Définit l'étape active
	 * @returns {void}
	 */
	private setActiveStep(): void {
		Object.keys(this.steps).forEach((key) => {
			const currentKey = parseInt(key);
			if (this.currentStep == currentKey) {
				this.steps[currentKey].active = true;
			} else {
				this.steps[currentKey].active = false;
			}
		});
	}

	/**
	 * Retourne la classe CSS pour un nombre donné (détracteur, passif, promoteur)
	 * @param value Valeur de 0 à 10
	 */
	public getNpsClass(value: number): string {
		if (value <= 6) {
			return "detractor";
		}
		if (value <= 8) {
			return "passive";
		}

		return "promoter";
	}

	/**
	 * Envoie l'enquête de satisfaction
	 * @returns {void}
	 */
	public sendSafistaction(): void {
		if (this.satisfactionData.Commentaire?.length == 0) {
			this.satisfactionData.Commentaire = null;
		}

		ServicesFactoryInstance.SatisfactionService.savePreviousSatisfactionSurvey(this.satisfactionData, () => {
			NotificationServiceInstance.SendSuccess("L'enquête de paie sous-traitée a bien été sauvegardée.");
			this.nextStep();
		}, () => {
			NotificationServiceInstance.SendFailed("Une erreur est survenue, l'enquête n'a pas été enregistrée.");
			this.hide();
		});
	}

	/**
	 * Retourne si le segment est complété ou non (100% ou 0%)
	 * @param index
	 * @returns {number}
	 */
	public getSegmentPercentage(index: number): number {
		if (this.currentStep >= index + 1) {
			return 100;
		}

		return 0;
	}
}



// WEBPACK FOOTER //
// ./src/components/modalComponents/modalEnqueteSatisfactionPst/modalEnqueteSatisfactionPst.ts